import { graphql } from "gatsby";
import React, { FC, useCallback } from "react";

import { useDataLayer } from "../../hooks/use-data-layer";
import { useFormattedDate } from "../../hooks/use-formatted-date";
import { GoogleAnalyticsEvent } from "../../utils/analytics";
import { Icon } from "../icon";
import { getAssetFormat } from "./get-asset-format";

export interface FileCardProps extends Queries.FileDataFragment {}

export const FileCard: FC<FileCardProps> = ({
  name,
  year,
  date: dateString,
  defaultLocaleEntry,
  file,
}) => {
  const { pushEvent } = useDataLayer();
  const onDownloadFile = useCallback(() => {
    if (defaultLocaleEntry) {
      pushEvent({
        event: GoogleAnalyticsEvent.FileDownload,
        file_name: defaultLocaleEntry.name,
        file_category: defaultLocaleEntry.category?.name,
      });
    }
  }, [defaultLocaleEntry, pushEvent]);

  const formattedLongDate = useFormattedDate(dateString, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const formattedShortDate = useFormattedDate(dateString, {
    month: "short",
    day: "numeric",
  });

  if (!file?.url) return null;

  return (
    <div className="flex gap-2 pl-3 w-full border border-primary-light items-center bg-light rounded-lg overflow-hidden">
      <div className="font-light flex-[0_0_10%] flex flex-col lg:flex-row gap-1 text-sm">
        <span className="whitespace-nowrap truncate hidden lg:block">
          {formattedLongDate || year}
        </span>
        <span className="whitespace-nowrap truncate lg:hidden">{formattedShortDate || year}</span>
      </div>
      <div className="flex-1 px-1 font-bold line-clamp-2">{name}</div>
      <div className="font-light hidden lg:block">{getAssetFormat(file?.mimeType)}</div>
      <div className="flex p-4 bg-primary-light h-full justify-center items-center">
        <a href={file.url} onClick={onDownloadFile} target="_blank" rel="noreferrer">
          <Icon icon="download" className="w-8 h-8 text-primary" />
        </a>
      </div>
    </div>
  );
};

export const query = graphql`
  fragment FileData on ContentfulFile {
    name
    date
    year
    node_locale
    defaultLocaleEntry {
      name
      category {
        name
      }
    }
    file {
      url
      mimeType
    }
  }
`;
